import dayjs from 'dayjs';
import i18n from 'i18n';
import { availableLanguages } from 'lib/utils';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { getCookie } from 'utils/utils.ts';

// Check if Language exists in available Languages
const checkLang = (language: string) =>
	availableLanguages.some((item) => item.lang_iso_code === language);

// Get Language from URL
const getUrlLang = (url?: string) => {
	if (!url && typeof window === 'undefined') return;
	const match = (url ?? window.location.pathname).match(/^\/([a-zA-Z]{2})\//);
	if (match) {
		if (checkLang(match[1])) {
			return match[1];
		}
	}
	return undefined;
};

// Get current language
const currentLang = () => {
	const urlLang = getUrlLang();
	if (urlLang) {
		return urlLang;
	}

	const cookieLang = getCookie('NEXT_LOCALE');
	if (cookieLang) {
		return cookieLang;
	} else if (i18n.language) {
		return i18n.language;
	} else {
		return 'en';
	}
};

const composeUrlLang = (pathname: string | undefined) => {
	if (!pathname) return '';
	if (/^https?:\/\//i.test(pathname)) {
		return pathname;
	}

	pathname = `/${pathname.replace(/^\/(.+)/, '$1')}`;

	const lang = currentLang();
	if (getUrlLang(pathname)) {
		return pathname.replace(/^\/[a-zA-Z]{2}/, `/${lang}`);
	} else {
		return `/${lang}${pathname}`;
	}
};

export function useLang() {
	const [routeTranslations, setRouteTranslations] = useState([]);
	const pathname = usePathname();
	const router = useRouter();

	const translatePathname = useCallback(
		(
			pathname: string | null | undefined,
			language: string,
			fromLanguage: string
		) => {
			if (!pathname) return pathname;

			return pathname
				.split('/')
				.map((segment: string, index: number) => {
					if (index == 1) {
						if (checkLang(segment)) {
							return language;
						}
					}

					const translation = routeTranslations.find((entry) => {
						return entry[fromLanguage] == segment;
					});
					if (translation) {
						return translation[language];
					} else {
						return segment;
					}
				})
				.join('/');
		},
		[routeTranslations]
	);

	const changeLang = (language: string) => {
		if (checkLang(language)) {
			const fromLanguage = i18n.language;
			i18n.changeLanguage(language);
			dayjs.locale(language);
			if (typeof window !== 'undefined') {
				document.cookie = `NEXT_LOCALE=${language}; path=/; max-age=31536000`;
			}

			const translatedPath = translatePathname(
				pathname,
				language,
				fromLanguage
			);
			router.push(
				`${translatedPath}${window.location.search}${window.location.hash}`
			);
			if (typeof window !== 'undefined') {
				document.documentElement.setAttribute('lang', language);
			}
		}
	};

	const getRouteTranslations = async () => {
		const response = await fetch(
			process.env.API_URL + 'v1/glob/route_translations'
		);
		const result = await response.json();
		if (result.routeTranslations) {
			setRouteTranslations(result.routeTranslations);
		}
	};

	useEffect(() => {
		getRouteTranslations();
	}, []);

	return {
		translatePathname,
		changeLang,
		currentLang: currentLang(),
		checkLang,
		getUrlLang: getUrlLang(),
		composeUrlLang,
	};
}
